/**
 * 治愈系空状态组件
 * 使用 SVG 手绘风格插画，配合温馨文案
 */

import React from 'react';
import './EmptyWarm.css';

interface EmptyWarmProps {
  type?: 'default' | 'search' | 'data' | 'network';
  description?: string;
  extra?: React.ReactNode;
  style?: React.CSSProperties;
}

const EmptyWarm: React.FC<EmptyWarmProps> = ({
  type = 'default',
  description,
  extra,
  style,
}) => {
  // 根据类型选择不同的插画和文案
  const configs = {
    default: {
      icon: CatOnDeskIcon,
      description: description || '这里还是空空的，添加点内容吧～',
    },
    search: {
      icon: CloudSearchIcon,
      description: description || '没有找到相关的内容呢～',
    },
    data: {
      icon: GrowingPlantIcon,
      description: description || '数据正在努力生长中，请稍候...',
    },
    network: {
      icon: SleepingCatIcon,
      description: description || '网络不太给力，刷新试试？',
    },
  };

  const { icon: Icon, description: desc } = configs[type];

  return (
    <div className="warm-empty-container" style={style}>
      <div className="warm-empty-icon-wrapper">
        <Icon />
      </div>
      <div className="warm-empty-text">{desc}</div>
      {extra && <div className="warm-empty-extra">{extra}</div>}
    </div>
  );
};

export default EmptyWarm;

// ========== 插画组件 ==========

const CatOnDeskIcon: React.FC = () => (
  <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
    {/* 办公桌 */}
    <rect x="20" y="90" width="80" height="10" rx="5" fill="#F9EBD8" />
    <rect x="30" y="95" width="60" height="3" rx="1.5" fill="#D4C0A8" />

    {/* 电脑 */}
    <rect x="45" y="70" width="30" height="20" rx="3" fill="#FFF5E6" />
    <rect x="47" y="72" width="26" height="16" rx="2" fill="#E8D5C4" />

    {/* 小猫 */}
    <circle cx="35" cy="65" r="12" fill="#F8D7DA" />
    <circle cx="32" cy="63" r="2" fill="#5C4033" />
    <circle cx="37" cy="63" r="2" fill="#5C4033" />
    <path d="M33 68 Q35 70 37 68" stroke="#5C4033" strokeWidth="1.5" fill="none" />

    {/* 盆栽 */}
    <rect x="75" y="80" width="12" height="8" rx="2" fill="#D4EDDA" />
    <circle cx="81" cy="75" r="6" fill="#8FBC8F" />
  </svg>
);

const CloudSearchIcon: React.FC = () => (
  <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
    {/* 云朵 */}
    <path
      d="M60 30 C45 30 35 40 35 55 C35 65 45 75 60 75 C75 75 85 65 85 55 C85 40 75 30 60 30 Z"
      fill="#FFF5E6"
      stroke="#D4C0A8"
      strokeWidth="2"
    />
    <circle cx="50" cy="45" r="3" fill="#F8D7DA" />
    <circle cx="70" cy="45" r="3" fill="#D4EDDA" />

    {/* 放大镜 */}
    <circle cx="60" cy="60" r="15" fill="none" stroke="#D4C0A8" strokeWidth="2" />
    <line x1="60" y1="60" x2="72" y2="72" stroke="#D4C0A8" strokeWidth="2" />
  </svg>
);

const GrowingPlantIcon: React.FC = () => (
  <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
    {/* 花盆 */}
    <rect x="40" y="80" width="40" height="20" rx="5" fill="#F9EBD8" />
    <rect x="45" y="85" width="30" height="3" rx="1.5" fill="#D4C0A8" />

    {/* 泥土 */}
    <rect x="42" y="75" width="36" height="8" rx="4" fill="#8B7355" />

    {/* 幼苗 */}
    <rect x="58" y="50" width="4" height="25" rx="2" fill="#8FBC8F" />
    <path
      d="M55 55 Q60 45 65 55 L63 55 Q60 50 57 55 Z"
      fill="#D4EDDA"
    />
    <path
      d="M53 60 Q60 50 67 60 L65 60 Q60 55 55 60 Z"
      fill="#D4EDDA"
    />

    {/* 阳光 */}
    <circle cx="60" cy="30" r="8" fill="#FFF3CD" />
    <line x1="60" y1="20" x2="60" y2="15" stroke="#FFF3CD" strokeWidth="2" />
    <line x1="60" y1="40" x2="60" y2="45" stroke="#FFF3CD" strokeWidth="2" />
    <line x1="50" y1="30" x2="45" y2="30" stroke="#FFF3CD" strokeWidth="2" />
    <line x1="70" y1="30" x2="75" y2="30" stroke="#FFF3CD" strokeWidth="2" />
  </svg>
);

const SleepingCatIcon: React.FC = () => (
  <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
    {/* 小猫睡觉 */}
    <circle cx="60" cy="60" r="25" fill="#F8D7DA" />
    <circle cx="52" cy="55" r="3" fill="#5C4033" />
    <circle cx="68" cy="55" r="3" fill="#5C4033" />

    {/* Zzz 符号 */}
    <path d="M55 40 L60 35 L55 30" stroke="#5C4033" strokeWidth="2" fill="none" />
    <path d="M65 40 L70 35 L65 30" stroke="#5C4033" strokeWidth="2" fill="none" />

    {/* 枕头 */}
    <rect x="35" y="75" width="50" height="15" rx="7.5" fill="#FFF5E6" />
    <circle cx="50" cy="82" r="2" fill="#F8D7DA" />
    <circle cx="70" cy="82" r="2" fill="#F8D7DA" />
  </svg>
);
